import type { ComponentProps, PropsWithChildren, ReactNode } from "react";
import { AnimatePresence, motion } from "motion/react";
import { Slot } from "radix-ui";
import type { VariantProps } from "tailwind-variants";
import { tv } from "tailwind-variants";

import { Logo } from "@/assets";
import { Collapse, FadeOut } from "@/shared/ui/animation";
import { Button, ScrollArea, Text } from "@/shared/ui/common";

const headerStyles = tv({
  slots: {
    root: "w-full bg-transparent",
    container: [
      "grid h-16 grid-cols-[minmax(0,1fr)_auto_auto] items-center justify-center gap-x-3 px-5 transition-all duration-150 sm:h-20 sm:grid-cols-2 sm:px-14",
      "has-[[data-slot=left-slot]]:[&>[data-slot=levels-rx-logo]]:col-span-1 has-[[data-slot=left-slot]]:[&>[data-slot=levels-rx-logo]]:justify-start has-[[data-slot=right-slot]]:[&>[data-slot=levels-rx-logo]]:col-span-1 has-[[data-slot=right-slot]]:[&>[data-slot=levels-rx-logo]]:justify-start",
    ],
    left: "col-start-2 row-start-1 justify-self-end sm:col-start-1 sm:justify-self-start",
    right: "col-start-3 row-start-1 justify-self-end",
    logo: "col-span-full col-start-1 flex h-8 justify-center text-secondary-800 sm:absolute sm:left-1/2 sm:h-20 sm:-translate-x-1/2 [&>*]:w-fit",
    progressTrack:
      "h-0.5 w-full has-[[data-slot=progress-bar]]:bg-primary-100 sm:h-1",
    progressBar: "h-full bg-primary-400",
  },
});

const { root, container, left, right, logo, progressTrack, progressBar } =
  headerStyles();

type HeaderProps = {
  progress?: number;
  leftSlot?: ReactNode;
  rightSlot?: ReactNode;
} & Pick<ComponentProps<typeof Logo>, "theme">;

const Header = ({ progress, leftSlot, rightSlot, theme }: HeaderProps) => {
  return (
    <div className={root()}>
      <div className={container()}>
        {leftSlot && (
          <Slot.Root data-slot="left-slot" className={left()}>
            {leftSlot}
          </Slot.Root>
        )}

        <div data-slot="levels-rx-logo" className={logo()}>
          <Logo className="!w-25.5 sm:my-auto sm:h-8" theme={theme} />
        </div>

        {rightSlot && (
          <Slot.Root data-slot="right-slot" className={right()}>
            {rightSlot}
          </Slot.Root>
        )}
      </div>

      <div className={progressTrack()}>
        {progress && (
          <motion.div
            data-slot="progress-bar"
            className={progressBar()}
            initial={{ width: `${progress}%` }}
            animate={{ width: `${progress}%` }}
            transition={{
              duration: 0.3,
              ease: "linear",
            }}
          />
        )}
      </div>
    </div>
  );
};

export const LAYOUT_SCROLL_VIEWPORT_ID = "layout-scroll-viewport";

const contentStyles = tv({
  slots: {
    main: "grow overflow-hidden",
    scrollArea: "px-5 py-6 sm:py-16",
    content:
      "mx-auto flex w-full flex-col items-center gap-6 sm:max-w-4xl sm:gap-10",
  },
  variants: {
    size: {
      sm: {
        content: "sm:max-w-xl",
      },
    },
  },
});

const Content = ({
  size,
  children,
}: PropsWithChildren & VariantProps<typeof contentStyles>) => {
  const { main, scrollArea, content } = contentStyles({ size });

  return (
    <div className={main()}>
      <ScrollArea id={LAYOUT_SCROLL_VIEWPORT_ID} className={scrollArea()}>
        <FadeOut className={content()}>{children}</FadeOut>
      </ScrollArea>
    </div>
  );
};

const footerStyles = tv({
  base: [
    "flex flex-col gap-2.5 border-t border-primary-100 px-5 py-6 sm:flex-row sm:items-center sm:justify-end sm:px-16",
    "sm:has-[[data-slot=cancel]]:justify-between [&>[data-slot=action]]:order-1 sm:[&>[data-slot=action]]:order-3 [&>[data-slot=cancel]]:order-3 sm:[&>[data-slot=cancel]]:order-1 [&>[data-slot=message]]:order-1 sm:[&>[data-slot=message]]:order-2",
  ],
});

type FooterProps = PropsWithChildren & {
  message?: string;
};

const Footer = ({ message, children }: FooterProps) => {
  return (
    <div className={footerStyles()}>
      <AnimatePresence>
        {message && (
          <Collapse key="error-message" data-slot="message">
            <Text align="center" color="error" as="p">
              {message}
            </Text>
          </Collapse>
        )}
      </AnimatePresence>

      {children}
    </div>
  );
};

const Cancel = ({
  ...props
}: Omit<ComponentProps<typeof Button>, "size" | "className">) => {
  return (
    <Button
      data-slot="cancel"
      variant="secondary"
      size="lg"
      className="min-w-32"
      {...props}
    />
  );
};

const Action = ({
  ...props
}: Omit<ComponentProps<typeof Button>, "size" | "className">) => {
  return (
    <Button data-slot="action" size="lg" className="min-w-32" {...props} />
  );
};

export const Layout = {
  Content,
  Header,
  Footer,
  Cancel,
  Action,
};
